/**
 * Score formatting utilities for displaying score breakdowns
 * Used by AccusationModal and CaseSummaryModal
 */

/**
 * Formats a numeric value with appropriate sign prefix
 * For adjustments that can be positive (bonus) or negative (penalty)
 * @param {number|null|undefined} value - The value to format
 * @returns {string} Formatted value with + or - prefix (or '0' without prefix for zero)
 */
export const formatSignedValue = (value) => {
  if (value === undefined || value === null) return '0';
  if (value === 0) return '0';
  return value >= 0 ? `+${value}` : `${value}`;
};

/**
 * Formats a penalty value (assumes positive penalties, adds minus sign)
 * Handles the edge case of "-0" by returning "0" explicitly
 * @param {number|null|undefined} penalty - The penalty value
 * @returns {string} Formatted penalty with - prefix for positive values
 */
export const formatPenalty = (penalty) => {
  if (penalty === undefined || penalty === null) return '0';
  if (penalty === 0) return '0';
  return penalty > 0 ? `-${penalty}` : `${penalty}`;
};

/**
 * Returns appropriate Tailwind color class based on value sign
 * Positive/zero values are green (good), negative values are red (bad)
 * @param {number|null|undefined} value - The value to evaluate
 * @returns {string} 'text-green-400' for positive/zero, 'text-red-400' for negative
 */
export const getSignedColorClass = (value) => {
  return (value ?? 0) >= 0 ? 'text-green-400' : 'text-red-400';
};

/**
 * Builds the `evidence_relevance` breakdown row, or null when it must not render.
 *
 * The term answers "did what you found actually implicate the person you accused?"
 * Two fields gate the copy, and neither can be inferred from the numbers:
 *
 * - `revealed` is false while the accusation is unresolved. The whole section is
 *   zeroed on purpose then (it would otherwise be a killer oracle for the polled
 *   mid-game score), so a zero means "not graded yet", not "found none". Render
 *   nothing at all.
 * - `documents_gradable` is false when the case carries no usable skeleton. On
 *   those cases documents CANNOT be assessed, which is a different statement from
 *   "you found no implicating documents" — so the row narrows to clues rather
 *   than reporting a document count the backend never actually graded.
 *
 * Counts only. Never name or list which pieces implicate the killer.
 *
 * @param {object|null|undefined} relevance - `breakdown.evidence_relevance`
 * @returns {{label: string, note: string|null, bonus: number}|null}
 */
export const getEvidenceRelevanceRow = (relevance) => {
  if (!relevance || relevance.revealed !== true) return null;

  const found = relevance.found ?? 0;
  const required = relevance.required ?? 0;
  const gradable = relevance.documents_gradable === true;

  return {
    label: gradable
      ? `Implicating Evidence (${found} found, ${required} needed)`
      : `Implicating Clues (${found} found, ${required} needed)`,
    note: gradable
      ? null
      : "This case's documents don't count here — clues only.",
    bonus: relevance.bonus ?? 0,
  };
};

/**
 * The one-line verdict on whether the accusation was actually established by the
 * evidence the player held.
 *
 * An under-evidenced accusation is ALLOWED and still reads as correct — it simply
 * scores lower, and this line is where the results screen says so. It is written
 * as arriving ahead of the proof, never as a scolding: until evidence
 * reachability is guaranteed, some cases genuinely cannot yield the required
 * implicating pieces without paying the score-penalized guide consult.
 *
 * Returns null when there is nothing honest to add — an unresolved accusation, or
 * a wrong accusation that was ALSO under-evidenced, where the existing
 * wrong-accusation copy already carries the weight and a second line would only
 * pile on.
 *
 * @param {object} args
 * @param {object|null|undefined} args.relevance - `breakdown.evidence_relevance`
 * @param {boolean} args.isCorrect - whether the accused suspect was the killer
 * @returns {{tone: 'affirm'|'note', text: string}|null}
 */
export const getEvidenceVerdict = ({ relevance, isCorrect }) => {
  if (!relevance || relevance.revealed !== true) return null;

  const sufficient = relevance.sufficient === true;

  if (isCorrect) {
    return sufficient
      ? {
          tone: 'affirm',
          text: 'Right person, and the proof to back it. The evidence in your file carries the accusation on its own.',
        }
      : {
          tone: 'note',
          text: "Right person, ahead of the proof. You called it before the evidence in your file could carry it — good read, but the paper trail hadn't caught up. The score reflects that gap, not the call.",
        };
  }

  if (sufficient) {
    return {
      tone: 'note',
      text: 'The evidence to solve this case was already in your file.',
    };
  }

  return null;
};
